/**
 * Category-aware field-visibility rubric.
 *
 * Defines which spec fields are *relevant* for each part category so
 * the create / edit / drawer surfaces can hide rows that don't apply.
 * Aaron's rule: irrelevant fields hide; relevant-but-blank fields stay
 * visible because a blank is a gap signal.
 *
 * The single source of truth so NewPartModal, EditPartModal, the
 * drawer's "show empty fields" mode, and (future) bulk-refresh's
 * vendor-diff filter all behave consistently. When adding a new
 * specialty field:
 *
 *  1. Add it to the `SpecialtyField` union below.
 *  2. List the categories that should show it in `FIELD_RELEVANCE`.
 *  3. Map it to its Part attribute(s) in `SPECIALTY_PART_FIELDS`.
 *
 * Categories with no entry in `FIELD_RELEVANCE` get the strict empty-
 * list treatment — only the always-visible "common" fields render.
 * "No category yet" is treated as "show everything" so a fresh part
 * doesn't lose data-entry surface before the user picks a category.
 */

export type SpecialtyField =
  | 'dielectric'
  | 'vf'
  | 'vr'
  | 'power_w'
  | 'polarity'
  | 'regulator_output'
  | 'voltage_clamp'
  | 'fuse_currents'
  | 'led_color'
  | 'pitch'
  | 'luminous_intensity'
  | 'load_capacitance'
  | 'rds_on'
  | 'hfe';

export const FIELD_RELEVANCE: Record<string, SpecialtyField[]> = {
  // Capacitors — only ceramic/film carry a dielectric the user
  // filters on; electrolytic/tantalum/polymer don't (they're defined
  // by their construction, which the category itself names).
  capacitor: ['dielectric'],
  'capacitor-ceramic': ['dielectric'],
  'capacitor-electrolytic': [],
  'capacitor-tantalum': [],
  'capacitor-film': ['dielectric'],
  'capacitor-polymer': [],
  'capacitor-trimmer': [],
  // Resistors + variable resistors.
  resistor: ['power_w'],
  'resistor-thick-film': ['power_w'],
  'resistor-thin-film': ['power_w'],
  'resistor-wirewound': ['power_w'],
  potentiometer: ['power_w'],
  // Discrete semiconductors. TVS gets `voltage_clamp` because
  // clamping voltage is the spec designers actually filter on, and
  // `polarity` for the unidirectional/bidirectional split.
  diode: ['vf', 'vr'],
  'diode-schottky': ['vf', 'vr'],
  'diode-zener': ['vf', 'vr'],
  'diode-tvs': ['vf', 'vr', 'voltage_clamp', 'polarity'],
  'diode-bridge': ['vf', 'vr'],
  led: ['vf', 'led_color', 'luminous_intensity'],
  'transistor-bjt': ['power_w', 'polarity', 'hfe'],
  mosfet: ['power_w', 'polarity', 'rds_on'],
  optocoupler: ['vf'],
  // Switching / power-handling. Fuse uses the dedicated hold/trip
  // current pair instead of the generic `power_w`.
  switch: ['power_w'],
  power: ['power_w'],
  relay: ['power_w'],
  fuse: ['fuse_currents'],
  transformer: ['power_w'],
  // Voltage regulators get the dedicated output-spec block (voltage,
  // current, fixed/adjustable). `power_w` is rarely the primary
  // filter for an LDO so it's dropped — switch on "Show all fields"
  // if you need it.
  'voltage-regulator': ['regulator_output'],
  'op-amp': ['power_w'],
  'ic-pmic': ['power_w'],
  'ic-audio': ['power_w'],
  // Connector / header pitch. Mechanical sometimes covers FFC cables
  // that also have a pitch, so it gets the same treatment.
  connector: ['pitch'],
  mechanical: ['pitch'],
  // Crystals: load capacitance is the spec users match against the
  // MCU's oscillator stage; far more useful than frequency stability.
  crystal: ['load_capacitance'],
  // Everything else (Inductor, IC, Microcontroller, Logic, Memory,
  // Sensor, Antenna, Battery, Other) stays out of the dict and gets
  // the empty-list strict treatment.
};

/**
 * Map a specialty field family to the underlying Part attribute(s)
 * it gates. Used by the drawer + EditPartModal to hide whole rows
 * when the category doesn't include the family.
 *
 * Some specialty fields gate multiple Part columns
 * (regulator_output, fuse_currents, luminous_intensity).
 */
export const SPECIALTY_PART_FIELDS: Record<SpecialtyField, readonly string[]> = {
  dielectric: ['dielectric'],
  vf: ['vf'],
  vr: ['vr'],
  power_w: ['power_w'],
  polarity: ['polarity'],
  regulator_output: [
    'output_voltage_v',
    'output_voltage_v_max',
    'output_current_a',
    'regulator_output_type',
  ],
  voltage_clamp: ['voltage_clamp_v'],
  fuse_currents: ['hold_current_a', 'trip_current_a'],
  led_color: ['color'],
  pitch: ['pitch_mm'],
  luminous_intensity: ['luminous_intensity_mcd', 'wavelength_nm'],
  load_capacitance: ['load_capacitance_pf'],
  rds_on: ['rds_on_ohms'],
  hfe: ['hfe'],
};

export function isSpecialtyRelevant(
  field: SpecialtyField,
  categorySlug: string | null,
  showAll = false,
): boolean {
  if (showAll) return true;
  if (!categorySlug) return true;
  const relevant = FIELD_RELEVANCE[categorySlug];
  if (relevant === undefined) return false;
  return relevant.includes(field);
}

/**
 * Convenience for the drawer / edit modal: returns true if the given
 * Part attribute corresponds to a specialty family the category
 * doesn't claim. Common fields that aren't specialty-gated (mfg_pn,
 * package, voltage_v, current_a, etc.) always pass.
 */
export function isPartFieldHidden(
  partField: string,
  categorySlug: string | null,
  showAll = false,
): boolean {
  if (showAll) return false;
  if (!categorySlug) return false;
  for (const [specialty, partFields] of Object.entries(SPECIALTY_PART_FIELDS)) {
    if (partFields.includes(partField)) {
      return !isSpecialtyRelevant(specialty as SpecialtyField, categorySlug, showAll);
    }
  }
  return false;
}
